// ResponsiveImage.jsx
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { THEME } from "@shared/constants/designSystem";
import { cn } from "@shared/lib/utils";

function ResponsiveImage({
  src,
  alt,
  className = "",
  loading = "lazy",
  sizes = "100vw",
  onImageLoad = () => {},
  onImageError = () => {},
}) {
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [imageSrc, setImageSrc] = useState(src);

  // Ensure we have a proper alt text
  const accessibleAlt = alt || "Image";

  // Simulate progress for better UX
  useEffect(() => {
    if (isLoading && !error) {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 10;
        if (progress > 90) {
          clearInterval(interval);
        } else {
          setLoadProgress(Math.min(progress, 90));
        }
      }, 200);

      return () => clearInterval(interval);
    }
  }, [isLoading, error]);

  useEffect(() => {
    setImageSrc(src);
    setIsLoading(true);
    setError(false);
    setLoadProgress(0);
  }, [src]);

  const handleLoad = () => {
    setIsLoading(false);
    setLoadProgress(100);
    onImageLoad();
  };

  const handleError = () => {
    console.warn(`Failed to load image: ${src}`);
    setError(true);
    setIsLoading(false);
    onImageError();
  };

  const handleRetry = () => {
    setError(false);
    setIsLoading(true);
    setLoadProgress(0);

    // Force reload by adding a cache-busting parameter
    const cacheBuster = Date.now();
    const separator = src.includes("?") ? "&" : "?";
    setImageSrc(`${src}${separator}cb=${cacheBuster}`);
  };

  return (
    <div className="relative w-full h-full overflow-hidden">
      {!error && (
        <img
          src={imageSrc}
          alt={accessibleAlt}
          loading={loading}
          sizes={sizes}
          onError={handleError}
          onLoad={handleLoad}
          className={cn(
            "w-full h-full object-cover",
            isLoading ? "opacity-0" : "opacity-100",
            "transition-all duration-500 ease-out",
            className,
          )}
          style={{ objectFit: "cover" }}
        />
      )}

      {/* Accessibility: Status updates for screen readers */}
      <div aria-live="polite" className="sr-only">
        {isLoading ? `Loading image: ${accessibleAlt}` : ""}
        {error ? `Error loading image: ${accessibleAlt}` : ""}
      </div>

      {isLoading && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-800 bg-opacity-40 dark:bg-opacity-40"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={Math.round(loadProgress)}
          aria-label={`Loading ${accessibleAlt}, ${Math.round(loadProgress)}% complete`}
        >
          <div className="flex flex-col items-center gap-2">
            <div className="h-10 w-10 relative">
              <div className="absolute inset-0 rounded-full border-4 border-gray-200 dark:border-gray-700"></div>
              <div
                className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent"
                style={{
                  transform: `rotate(${loadProgress * 3.6}deg)`,
                  transition: "transform 0.3s ease-out",
                }}
              ></div>
            </div>
            <span className={`text-sm ${THEME.COLORS.TEXT.MUTED}`}>
              Loading...
            </span>
          </div>
        </div>
      )}

      {error && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center bg-red-50 dark:bg-red-900 bg-opacity-90 dark:bg-opacity-30"
          role="alert"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-10 w-10 text-red-500 mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p className="text-sm text-gray-900 dark:text-gray-100 mb-2">
            Failed to load image
          </p>
          <button
            onClick={handleRetry}
            className={`px-3 py-1 text-sm rounded ${THEME.COLORS.BG.PRIMARY} ${THEME.COLORS.TEXT.ON_PRIMARY} ${THEME.COLORS.HOVER.BG.PRIMARY}`}
            aria-label={`Retry loading ${accessibleAlt}`}
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}

ResponsiveImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  loading: PropTypes.oneOf(["lazy", "eager"]),
  sizes: PropTypes.string,
  onImageLoad: PropTypes.func,
  onImageError: PropTypes.func,
};

export default ResponsiveImage;
export { ResponsiveImage };
